import { useState } from 'react';
import dayjs from 'dayjs';
import { Badge, Button, ButtonBase, IconButton, Popover, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CheckIcon from '@mui/icons-material/Check';
import TodayIcon from '@mui/icons-material/Today';
import { BULK_ACTION_LABELS, PLANNING_LABELS as LABELS } from '../../../utils/labels';
import FilledIconButton from '../../../components/FilledIconButton';

interface DateNavigationProps {
  viewLabel: string;
  viewStart: string;
  canEdit: boolean;
  isReorderMode: boolean;
  isBulkMode: boolean;
  activeFilterCount: number;
  onFilterOpen: () => void;
  onShiftDays: (n: number) => void;
  onGoToday: () => void;
  onDateSelect: (date: string) => void;
  onAddMachine: () => void;
  onToggleReorderMode: () => void;
  onToggleBulkMode: () => void;
}

export default function DateNavigation({
  viewLabel,
  viewStart,
  canEdit,
  isReorderMode,
  isBulkMode,
  activeFilterCount,
  onFilterOpen,
  onShiftDays,
  onGoToday,
  onDateSelect,
  onAddMachine,
  onToggleReorderMode,
  onToggleBulkMode,
}: DateNavigationProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isWideDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    if (!value) return;
    onDateSelect(value.format('YYYY-MM-DD'));
    setAnchorEl(null);
  };

  const dateTrigger = (ref?: React.Ref<HTMLButtonElement>) => (
    <Tooltip title={LABELS.selectDate}>
      <ButtonBase
        ref={ref}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={LABELS.selectDate}
        className="flex items-center gap-1 px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
        data-testid="date-select-button"
      >
        <span className="font-semibold text-sm">{viewLabel}</span>
        <CalendarMonthOutlinedIcon sx={{ fontSize: '1rem', opacity: 0.6 }} />
      </ButtonBase>
    </Tooltip>
  );

  return (
    <>
      <div className="flex items-center gap-4 px-2 py-2 border-b border-border bg-surface flex-shrink-0">
        {isMobile ? (
          <>
            <Tooltip title={LABELS.filterTooltip}>
              <IconButton onClick={onFilterOpen} aria-label={LABELS.filterTooltip} data-testid="filter-button">
                <Badge badgeContent={activeFilterCount} color="primary">
                  <FilterListIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <div className="flex gap-1 items-center">
              <div className="gap-1 items-center hidden md:flex">
                <Tooltip title={LABELS.prevWeek}>
                  <IconButton onClick={() => onShiftDays(-7)} aria-label={LABELS.prevWeek} data-testid="prev-week-button">
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>

                {dateTrigger()}

                <Tooltip title={LABELS.nextWeek}>
                  <IconButton onClick={() => onShiftDays(7)} aria-label={LABELS.nextWeek} data-testid="next-week-button">
                    <ChevronRightIcon />
                  </IconButton>
                </Tooltip>
              </div>

              <Tooltip title={LABELS.today}>
                <IconButton onClick={onGoToday} aria-label={LABELS.today} data-testid="today-button">
                  <TodayIcon />
                </IconButton>
              </Tooltip>
            </div>
            <div className="flex-1" />

            {canEdit && !isReorderMode && (
              <Tooltip title={isBulkMode ? BULK_ACTION_LABELS.bulkModeExit : BULK_ACTION_LABELS.bulkMode}>
                <IconButton onClick={onToggleBulkMode} aria-label={isBulkMode ? BULK_ACTION_LABELS.bulkModeExit : BULK_ACTION_LABELS.bulkMode} color={isBulkMode ? 'primary' : 'default'} data-testid="bulk-mode-toggle-button">
                  {isBulkMode ? <CheckIcon /> : <ChecklistIcon />}
                </IconButton>
              </Tooltip>
            )}
            {canEdit && !isBulkMode && (
              <Tooltip title={LABELS.addMachine}>
                <span>
                  <FilledIconButton onClick={onAddMachine} aria-label={LABELS.addMachine} data-testid="add-machine-button">
                    <AddIcon />
                  </FilledIconButton>
                </span>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            <div className="grow flex gap-2 items-center px-2">
              <Tooltip title={LABELS.prevWeek}>
                <IconButton onClick={() => onShiftDays(-7)} aria-label={LABELS.prevWeek} data-testid="prev-week-button">
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>

              {dateTrigger()}

              <Tooltip title={LABELS.nextWeek}>
                <IconButton onClick={() => onShiftDays(7)} aria-label={LABELS.nextWeek} data-testid="next-week-button">
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>

              <Button variant="outlined" color="inherit" onClick={onGoToday} data-testid="today-button">
                {LABELS.today}
              </Button>
            </div>

            {canEdit && (
              <div className="flex items-center gap-2 ml-auto">
                {isWideDesktop ? (
                  <>
                    {!isBulkMode && (
                      <Button
                        variant={isReorderMode ? 'contained' : 'outlined'}
                        color={isReorderMode ? 'success' : 'inherit'}
                        startIcon={isReorderMode ? <CheckIcon /> : <SwapVertIcon />}
                        onClick={onToggleReorderMode}
                        data-testid="reorder-toggle-button"
                      >
                        {isReorderMode ? LABELS.reorderModeExit : LABELS.reorderMode}
                      </Button>
                    )}
                    {!isReorderMode && (
                      <Button
                        variant={isBulkMode ? 'contained' : 'outlined'}
                        color={isBulkMode ? 'primary' : 'inherit'}
                        startIcon={isBulkMode ? <CheckIcon /> : <ChecklistIcon />}
                        onClick={onToggleBulkMode}
                        data-testid="bulk-mode-toggle-button"
                      >
                        {isBulkMode ? BULK_ACTION_LABELS.bulkModeExit : BULK_ACTION_LABELS.bulkMode}
                      </Button>
                    )}
                    {!isReorderMode && !isBulkMode && (
                      <Button variant="contained" startIcon={<AddIcon />} onClick={onAddMachine} data-testid="add-machine-button">
                        {LABELS.addMachine}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {!isBulkMode && (
                      <Tooltip title={isReorderMode ? LABELS.reorderModeExit : LABELS.reorderMode}>
                        <IconButton
                          onClick={onToggleReorderMode}
                          aria-label={isReorderMode ? LABELS.reorderModeExit : LABELS.reorderMode}
                          color={isReorderMode ? 'success' : 'default'}
                          data-testid="reorder-toggle-button"
                        >
                          {isReorderMode ? <CheckIcon /> : <SwapVertIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {!isReorderMode && (
                      <Tooltip title={isBulkMode ? BULK_ACTION_LABELS.bulkModeExit : BULK_ACTION_LABELS.bulkMode}>
                        <IconButton
                          onClick={onToggleBulkMode}
                          aria-label={isBulkMode ? BULK_ACTION_LABELS.bulkModeExit : BULK_ACTION_LABELS.bulkMode}
                          color={isBulkMode ? 'primary' : 'default'}
                          data-testid="bulk-mode-toggle-button"
                        >
                          {isBulkMode ? <CheckIcon /> : <ChecklistIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {!isReorderMode && !isBulkMode && (
                      <Tooltip title={LABELS.addMachine}>
                        <span>
                          <FilledIconButton onClick={onAddMachine} aria-label={LABELS.addMachine} data-testid="add-machine-button">
                            <AddIcon />
                          </FilledIconButton>
                        </span>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <DateCalendar value={dayjs(viewStart)} onChange={handleDateChange} />
      </Popover>
    </>
  );
}
